import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const TeacherAttendance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [toast, setToast] = useState(null);
  const [modalReport, setModalReport] = useState(null);
  const [reportNotes, setReportNotes] = useState('');

  const token = localStorage.getItem('token');

  // Toast auto-hide
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  // Chargement des données
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(
        `/api/teacher/teacher-attendance?date=${date}`,
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 403) {
        throw new Error("Vous n'êtes pas professeur principal d'une classe");
      }
      if (!res.ok) {
        throw new Error('Erreur de chargement');
      }

      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  // Enregistrer une présence
  const saveAttendance = async (teacher, status) => {
    try {
      setSaving(true);
      const res = await fetch('/api/teacher/teacher-attendance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          teacher_id: teacher.teacher_id,
          assignment_id: teacher.assignment_id,
          date: date,
          status: status,
          scheduled_start_time: teacher.scheduled_start_time || '08:00',
          actual_arrival_time:
            status === 'present' || status === 'late'
              ? new Date().toTimeString().slice(0, 5)
              : null,
        }),
      });

      const json = await res.json();

      if (!res.ok) {
        showToast(json.error || 'Erreur', 'error');
      } else {
        showToast(`✅ ${teacher.teacher_name} marqué "${status}"`, 'success');
        fetchData();
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Signaler à l'administration
  const reportToAdmin = async () => {
    if (!modalReport) return;
    try {
      setSaving(true);
      const res = await fetch(
        `/api/teacher/teacher-attendance/${modalReport.attendance_id}/report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ notes: reportNotes }),
        }
      );

      const json = await res.json();

      if (!res.ok) {
        showToast(json.error || 'Erreur', 'error');
      } else {
        showToast("✅ Signalement envoyé à l'administration", 'success');
        setModalReport(null);
        setReportNotes('');
        fetchData();
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'present':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">🟢 Présent</span>;
      case 'late':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">🟠 En retard</span>;
      case 'absent':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">🔴 Absent</span>;
      case 'cancelled':
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">⚪ Annulé</span>;
      default:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">⏳ Non marqué</span>;
    }
  };

  // ============ RENDER ============
  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">
        Chargement...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-6 rounded-lg">
          <h2 className="font-bold mb-2">⚠️ {error}</h2>
          <p className="text-sm">
            Contactez l'administration si vous pensez qu'il s'agit d'une erreur.
          </p>
          <Link
            to="/teacher"
            className="inline-block mt-4 text-[#E67E22] hover:underline font-medium"
          >
            ← Retour au dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[100] ${
            toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 min-w-[280px]`}
        >
          <span className="flex-1 font-medium text-sm">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="text-white hover:text-gray-200 text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">
            🕐 Présence des enseignants
          </h1>
          <p className="text-gray-500 mt-1">
            Classe <strong>{data?.class?.name}</strong> — Suivi quotidien des profs
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="text-sm text-gray-600 font-medium">📅 Date :</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
          <div className="text-xs text-gray-500">Total profs</div>
          <div className="text-2xl font-bold text-[#2C3E50]">{data?.stats?.total || 0}</div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
          <div className="text-xs text-gray-500">🟢 Présents</div>
          <div className="text-2xl font-bold text-green-600">{data?.stats?.present || 0}</div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
          <div className="text-xs text-gray-500">🟠 En retard</div>
          <div className="text-2xl font-bold text-orange-600">{data?.stats?.late || 0}</div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
          <div className="text-xs text-gray-500">🔴 Absents</div>
          <div className="text-2xl font-bold text-red-600">{data?.stats?.absent || 0}</div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-gray-400">
          <div className="text-xs text-gray-500">⏳ Non marqués</div>
          <div className="text-2xl font-bold text-gray-600">{data?.stats?.not_marked || 0}</div>
        </div>
      </div>

      {/* Liste des profs */}
      {data?.teachers?.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-lg font-medium text-gray-700">
            Aucun professeur affecté à cette classe
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Contactez l'administration pour affecter des profs.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <h2 className="font-bold text-[#2C3E50]">
              👨‍🏫 Professeurs de la classe ({data?.teachers?.length})
            </h2>
          </div>

          <div className="divide-y divide-gray-100">
            {data.teachers.map((teacher) => (
              <div
                key={teacher.teacher_id}
                className="p-5 flex items-center justify-between flex-wrap gap-4 hover:bg-gray-50"
              >
                {/* Infos prof */}
                <div className="flex-1 min-w-[250px]">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-10 h-10 bg-[#E67E22] text-white rounded-full flex items-center justify-center font-bold">
                      {teacher.teacher_name?.charAt(0) || 'P'}
                    </div>
                    <div>
                      <div className="font-semibold text-[#2C3E50]">
                        {teacher.teacher_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {teacher.subject_name}
                      </div>
                    </div>
                  </div>
                  {teacher.scheduled_start_time && (
                    <div className="text-xs text-gray-500 mt-1">
                      🕐 Heure prévue : <strong>{teacher.scheduled_start_time}</strong>
                      {teacher.actual_arrival_time && (
                        <span className="ml-2">— Arrivée : <strong>{teacher.actual_arrival_time}</strong></span>
                      )}
                    </div>
                  )}
                </div>

                {/* Statut */}
                <div className="flex items-center gap-3 flex-wrap">
                  {getStatusBadge(teacher.status)}

                  {teacher.is_reported_to_admin && (
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                      📤 Signalé
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => saveAttendance(teacher, 'present')}
                    disabled={saving}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition disabled:opacity-50"
                  >
                    🟢 Présent
                  </button>
                  <button
                    onClick={() => saveAttendance(teacher, 'late')}
                    disabled={saving}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100 transition disabled:opacity-50"
                  >
                    🟠 En retard
                  </button>
                  <button
                    onClick={() => saveAttendance(teacher, 'absent')}
                    disabled={saving}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition disabled:opacity-50"
                  >
                    🔴 Absent
                  </button>

                  {teacher.status === 'absent' && teacher.attendance_id && !teacher.is_reported_to_admin && (
                    <button
                      onClick={() => {
                        setModalReport(teacher);
                        setReportNotes('');
                      }}
                      className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
                    >
                      📤 Signaler à l'admin
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Légende */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 text-sm text-blue-800">
        ℹ️ <strong>Rappel :</strong> Le prof principal constate et signale. Seule
        l'administration a le pouvoir de modifier officiellement le dossier d'un
        enseignant.
      </div>

      {/* ============ MODAL SIGNALER ============ */}
      {modalReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-bold text-[#2C3E50]">
                📤 Signaler à l'administration
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
                <div className="font-semibold mb-2">
                  🔴 Professeur absent
                </div>
                <div>
                  <strong>Prof :</strong> {modalReport.teacher_name}
                </div>
                <div>
                  <strong>Matière :</strong> {modalReport.subject_name}
                </div>
                <div>
                  <strong>Date :</strong> {date}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optionnel)
                </label>
                <textarea
                  value={reportNotes}
                  onChange={(e) => setReportNotes(e.target.value)}
                  rows="3"
                  placeholder="Précisions pour l'administration..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => setModalReport(null)}
                className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Annuler
              </button>
              <button
                onClick={reportToAdmin}
                disabled={saving}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50"
              >
                {saving ? '⏳ Envoi...' : '📤 Signaler'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAttendance;